import type { AgentMessage } from '../types'

export interface Caption {
  who: 'you' | 'bot' | null
  text: string
}

export const EMPTY_CAPTION: Caption = { who: null, text: '' }

/**
 * One turn of the conversation, as it should read on screen.
 *
 * The two speakers arrive differently and this is the whole reason this is a
 * function rather than a line inside a component:
 *
 *   - `bot` fires once per SENTENCE. A single reply is several messages, so they
 *     accumulate. Overwriting instead showed only the agent's last sentence —
 *     a real bug, found on a phone, already fixed once in the agent repo. It is
 *     visible on the very first connection, where the greeting arrives as
 *     "Hi there, welcome!" then "What can I get for you today?".
 *   - `user_interim` and `user_final` already carry the whole utterance so far,
 *     so they replace. An interim is simply overwritten by its final.
 *
 * Either speaker starting resets the other's buffer — that is the turn boundary,
 * and there is only ever one turn on screen.
 */
export function captionReducer(prev: Caption, msg: AgentMessage): Caption {
  if (msg.type === 'order') return prev

  const who: 'you' | 'bot' = msg.type === 'bot' ? 'bot' : 'you'
  if (who !== prev.who) return { who, text: msg.text }

  return who === 'you'
    ? { who, text: msg.text }
    : { who, text: prev.text ? `${prev.text} ${msg.text}` : msg.text }
}
